import { isDefined } from "@clipboard-health/util-ts";
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { logError } from "@src/appV2/lib/analytics";
import { useCallback, useMemo } from "react";

import { type ChatMessage } from "../types";
import { useMarkChannelAsUnread } from "./useMarkChannelAsUnread";

interface UseMarkAsUnreadHandlersParams {
  channel: GroupChannel | null | undefined;
  workplaceId: string;
  workerId: string;
  messages: readonly ChatMessage[];
  /**
   * Runs immediately after the target message is found — used by the panel
   * variant to close the drawer before the async SDK call so the parent's
   * auto-mark-read effect unmounts and can't race the unread update.
   */
  onAfterMark?: () => void;
}

interface UseMarkAsUnreadHandlersResult {
  handleMarkMessageAsUnread: (messageId: number) => void;
  handleMarkConversationAsUnread: () => void;
  canMarkConversationAsUnread: boolean;
  isMarkingAsUnread: boolean;
}

/**
 * Shared mark-as-unread handlers for the chat panel and the full-page chat
 * conversation. Marks from the provided message onward as unread via the
 * native Sendbird API, then invalidates the unread-count query.
 */
export function useMarkAsUnreadHandlers(
  params: UseMarkAsUnreadHandlersParams
): UseMarkAsUnreadHandlersResult {
  const { channel, workplaceId, workerId, messages, onAfterMark } = params;
  const { mutateAsync: markChannelAsUnread, isLoading: isMarkingAsUnread } = useMarkChannelAsUnread(
    { workplaceId, workerId }
  );

  const handleMarkMessageAsUnread = useCallback(
    (messageId: number) => {
      if (!channel) {
        return;
      }
      const target = messages.find((m) => m.messageId === messageId)?.sendbirdMessage;
      if (!target) {
        logError("Failed to find chat message to mark as unread", {
          error: new Error("Message not found"),
          metadata: { messageId },
        });
        return;
      }
      // Close the panel before the SDK call lands so useChatMessages
      // unmounts, its handlers detach, and its auto-mark-read effect can't
      // re-fire if a new message arrives mid-flight.
      onAfterMark?.();
      void (async () => {
        try {
          await markChannelAsUnread({ channel, message: target });
        } catch (error) {
          logError("Failed to mark chat message as unread", { error });
        }
      })();
    },
    [channel, markChannelAsUnread, messages, onAfterMark]
  );

  const lastIncomingMessageId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (
        isDefined(message.senderId) &&
        message.senderId !== workplaceId &&
        message.type !== "admin"
      ) {
        return message.messageId;
      }
    }
    return undefined;
  }, [messages, workplaceId]);

  const canMarkConversationAsUnread = Boolean(channel) && isDefined(lastIncomingMessageId);

  const handleMarkConversationAsUnread = useCallback(() => {
    if (!isDefined(lastIncomingMessageId)) {
      return;
    }
    handleMarkMessageAsUnread(lastIncomingMessageId);
  }, [handleMarkMessageAsUnread, lastIncomingMessageId]);

  return {
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    isMarkingAsUnread,
  };
}
